import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Download, Plus, Receipt, Trash } from "@/components/icons";
import {
  Badge,
  Button,
  confirm,
  DatePicker,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Skeleton,
  toast,
} from "@/components/kit";
import {
  Field as FieldLayout,
  FieldContent,
  FieldLabel,
} from "@/components/ui/field";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  type ExpenseCategoryDTO,
  type ExpenseListQueryDTO,
  expenseCategories,
} from "@/modules/expenses/domain/contracts";
import type { ExpenseDTO } from "@/modules/expenses/domain/types";
import { EXPENSE_CATEGORY_LABELS } from "@/modules/expenses/presentation/lib/category-labels";
import {
  buildCustomDateRange,
  buildDateRangePreset,
  reportRangeToPickerValue,
} from "@/modules/reports/domain/date-range-presets";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { expensesApi } from "../api/client";
import {
  useDeleteExpense,
  useExpensesQuery,
  useExpenseSummaryQuery,
} from "../api/queries";

const PAGE_SIZE = 20;
const ALL_CATEGORIES = "all";

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="brand-card flex flex-col gap-1 p-4">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-display text-2xl text-foreground tabular-nums">
        {value}
      </span>
      {sub ? <span className="text-muted-foreground text-xs">{sub}</span> : null}
    </div>
  );
}

export function ExpensesPage() {
  const nav = useNavigate({ from: "/app/expenses" });
  const [range, setRange] = useState(() => buildDateRangePreset("month"));
  const [category, setCategory] = useState<ExpenseCategoryDTO | undefined>();
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const canWrite = useActionPermission("expenses:write");
  const listQuery = useMemo<ExpenseListQueryDTO>(
    () => ({
      from: range.from,
      to: range.to,
      category,
      search: search.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [range, category, search, offset],
  );

  const expenses = useExpensesQuery(listQuery);
  const summary = useExpenseSummaryQuery({ from: range.from, to: range.to });
  const deleteExpense = useDeleteExpense();

  const rows = expenses.data?.data ?? [];
  const total = expenses.data?.meta.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  async function handleDelete(expense: ExpenseDTO) {
    const ok = await confirm({
      title: "ລຶບລາຍຈ່າຍ?",
      description: `${expense.title} — ${formatLak(expense.amount)}`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await deleteExpense.mutateAsync(expense.id);
      toast.success("ລຶບລາຍຈ່າຍແລ້ວ");
    } catch (e) {
      toast.error("ລຶບລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  async function handleExport() {
    try {
      setExporting(true);
      await expensesApi.downloadCsv(listQuery);
      toast.success("ດາວໂຫລດ CSV ສຳເລັດ");
    } catch (e) {
      toast.error("ດາວໂຫລດລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ລາຍຈ່າຍ
            </h2>
            <p className="text-muted-foreground text-sm">
              ບັນທຶກຄ່າແອດ Facebook ແລະ ລາຍຈ່າຍຮ້ານເພື່ອຄຳນວນກຳໄລສຸດທິ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            {canWrite ? (
              <Button
                className="shrink-0 rounded-full"
                onClick={() => nav({ to: "/app/expenses/new" })}
              >
                <Plus data-icon="inline-start" />
                ເພີ່ມລາຍຈ່າຍ
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="shrink-0 rounded-full"
              disabled={exporting}
              onClick={handleExport}
            >
              <Download />
              CSV ລາຍຈ່າຍ
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <SummaryCard
            label="ລາຍຈ່າຍທັງໝົດ"
            value={formatLak(summary.data?.totalExpenses ?? "0")}
            sub="ຕາມຊ່ວງວັນທີ່"
          />
          <SummaryCard
            label="Facebook / Marketing"
            value={formatLak(summary.data?.marketingExpenses ?? "0")}
            sub="ໃຊ້ຄຳນວນ MER"
          />
          <SummaryCard
            label="ລາຍການ"
            value={String(total)}
            sub="ລາຍຈ່າຍທີ່ filter ຢູ່"
          />
        </div>

        <section className="brand-card flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <FieldLayout className="sm:w-80">
              <FieldLabel>ຊ່ວງວັນທີ່</FieldLabel>
              <FieldContent>
                <DatePicker
                  mode="range"
                  value={reportRangeToPickerValue(range)}
                  onChange={(selected) => {
                    if (!selected?.from || !selected?.to) return;
                    setOffset(0);
                    setRange(
                      buildCustomDateRange(
                        selected.from,
                        selected.to,
                        range.basis,
                      ),
                    );
                  }}
                  captionLayout="dropdown"
                  fromYear={2024}
                  toYear={new Date().getFullYear() + 1}
                  className="w-full"
                />
              </FieldContent>
            </FieldLayout>
            <FieldLayout className="sm:w-56">
              <FieldLabel>ປະເພດ</FieldLabel>
              <FieldContent>
                <SimpleSelect
                  value={category ?? ALL_CATEGORIES}
                  onValueChange={(value) => {
                    setOffset(0);
                    setCategory(
                      value === ALL_CATEGORIES
                        ? undefined
                        : (value as ExpenseCategoryDTO),
                    );
                  }}
                  options={[
                    { value: ALL_CATEGORIES, label: "ທຸກປະເພດ" },
                    ...expenseCategories.map((c) => ({
                      value: c,
                      label: EXPENSE_CATEGORY_LABELS[c],
                    })),
                  ]}
                />
              </FieldContent>
            </FieldLayout>
            <FieldLayout className="min-w-0 flex-1">
              <FieldLabel>ຄົ້ນຫາ</FieldLabel>
              <FieldContent>
                <Input
                  value={search}
                  onChange={(e) => {
                    setOffset(0);
                    setSearch(e.target.value);
                  }}
                  placeholder="title, campaign, vendor..."
                />
              </FieldContent>
            </FieldLayout>
          </div>

          {expenses.isLoading ? (
            <div className="flex flex-col gap-3">
              {["a", "b", "c"].map((id) => (
                <Skeleton key={id} className="h-24 rounded-xl" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Empty className="py-10">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Receipt />
                </EmptyMedia>
                <EmptyTitle>ຍັງບໍ່ມີລາຍຈ່າຍ</EmptyTitle>
                <EmptyDescription>
                  ເພີ່ມຄ່າແອດ Facebook ຫຼື ລາຍຈ່າຍຮ້ານເພື່ອໃຫ້ກຳໄລສຸດທິຖືກຕ້ອງ
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ul className="flex flex-col gap-3">
              {rows.map((expense) => (
                <li
                  key={expense.id}
                  className="rounded-xl border border-secondary/20 bg-background/80 p-4"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-foreground">
                          {expense.title}
                        </p>
                        <Badge variant="secondary" className="rounded-full">
                          {EXPENSE_CATEGORY_LABELS[expense.category]}
                        </Badge>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground text-xs">
                        <span>{formatDateLocal(expense.spentAt)}</span>
                        {expense.vendor ? <span>{expense.vendor}</span> : null}
                        {expense.campaignName ? (
                          <span>{expense.campaignName}</span>
                        ) : null}
                      </div>
                      {expense.note ? (
                        <p className="mt-2 text-muted-foreground text-sm">
                          {expense.note}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center justify-between gap-2 sm:justify-end">
                      <span className="font-display text-secondary text-xl tabular-nums">
                        {formatLak(expense.amount)}
                      </span>
                      {canWrite ? (
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            onClick={() =>
                              nav({
                                to: "/app/expenses/$id/edit",
                                params: { id: expense.id },
                              })
                            }
                          >
                            ແກ້
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(expense)}
                            aria-label="ລຶບລາຍຈ່າຍ"
                          >
                            <Trash />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between border-secondary/15 border-t pt-3 text-muted-foreground text-sm">
            <span>
              {total === 0 ? "0" : `${offset + 1}-${Math.min(offset + PAGE_SIZE, total)}`}{" "}
              / {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={!hasPrev}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ກ່ອນໜ້າ
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={!hasNext}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                ຕໍ່ໄປ
              </Button>
            </div>
          </div>
        </section>
      </Main>
    </>
  );
}
